import { useEffect, useState } from 'react';

import {
  evaluateFeature,
  getSessionId,
  getUserId,
  putProjectEvent,
} from './evidently-connection';

export type ExperimentData<T extends string> = {
  FEATURE_NAME: string;
  DEFAULT_VALUE: T;
  RESULT_PROPERTY_NAME: string;
  variant: T | null;
  experimentSubmitted: boolean;
  isLoadingExperiment: boolean;
  loadingPromise: Promise<T> | null;
};

const generateUseVariant =
  <T extends string>(experimentData: ExperimentData<T>) =>
  () => {
    const [variant, setVariant] = useState(experimentData.DEFAULT_VALUE);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      getVariant(experimentData).then((variant) => {
        if (variant !== null) {
          setVariant(variant);
        }
        setIsLoading(false);
      });
    }, []);

    return { variant, isLoading };
  };

const getVariant = async <T extends string>(
  experimentData: ExperimentData<T>,
): Promise<T | null> => {
  if (experimentData.variant !== null) {
    return Promise.resolve(experimentData.variant);
  } else {
    if (!experimentData.isLoadingExperiment) {
      return loadFeature(experimentData);
    } else if (
      experimentData.isLoadingExperiment &&
      experimentData.loadingPromise !== null
    ) {
      return experimentData.loadingPromise;
    } else {
      return null;
    }
  }
};

const loadFeature = async <T extends string>({
  FEATURE_NAME: featureName,
  DEFAULT_VALUE,
  ...experimentData
}: ExperimentData<T>): Promise<T> => {
  experimentData.isLoadingExperiment = true;
  experimentData.loadingPromise = evaluateFeature(featureName)
    .catch((e) => {
      console.log(`Feature "${featureName}" not found. Using default variant.`);
      return null;
    })
    .then((res) => {
      experimentData.isLoadingExperiment = false;
      experimentData.loadingPromise = null;
      if (res === null || res.value?.stringValue === undefined) {
        return DEFAULT_VALUE;
      } else {
        experimentData.variant = res.value.stringValue as T;
        return experimentData.variant;
      }
    });

  return experimentData.loadingPromise;
};

const reset =
  <T extends string>(experimentData: ExperimentData<T>) =>
  async () => {
    if (!experimentData.experimentSubmitted) {
      await sendEvent(experimentData)(false);
    }
    experimentData.experimentSubmitted = false;
  };

const sendEvent =
  <T extends string>(experimentData: ExperimentData<T>) =>
  async (result: boolean) => {
    if (!experimentData.experimentSubmitted) {
      const userId = getUserId();
      const sessionId = getSessionId();

      experimentData.experimentSubmitted = true;
      const buttonClickedResult = JSON.stringify({
        details: {
          [experimentData.RESULT_PROPERTY_NAME]: result ? 1 : 0,
        },
        userDetails: { userId, sessionId },
      });

      await putProjectEvent(buttonClickedResult);
    }
  };

export const generateExperiment = <T extends string>(
  experimentData: ExperimentData<T>,
) => ({
  useVariant: generateUseVariant(experimentData),
  reset: reset(experimentData),
  sendEvent: sendEvent(experimentData),
});
